import React, { useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline } from '@mui/material';
import {
  SettingsProvider,
  defaultSettings,
} from '@wall-street-english-international/hello-design-system';
import '@wall-street-english-international/hello-design-system/dist/hello-design-system.css';
import { createAppTheme } from './theme';
import { useThemeStore } from './store/themeStore';
import App from './App';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error: unknown) => {
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'status' in error.response
        ) {
          const status = (error.response as { status: number }).status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

window.addEventListener('lessoncraft:ratelimit', (e) => {
  const detail = (e as CustomEvent<{ message: string }>).detail;
  console.warn('[RateLimit]', detail.message);
});

/** Reads dark-mode preference from the persisted Zustand store and injects
 *  the correct MUI theme so every component in the tree re-renders on toggle. */
const ThemedApp: React.FC = () => {
  const { mode } = useThemeStore();
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <SettingsProvider
      defaultSettings={{
        ...defaultSettings,
        themeMode: 'light',
        themeDirection: 'ltr',
        themeContrast: 'default',
        themeLayout: 'vertical',
        themeColorPresets: 'default',
        themeStretch: false,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemedApp />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SettingsProvider>
  </React.StrictMode>
);
